import { ChefHat, Edit, Flame, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { CarregandoTabela } from "@/components/carregando";
import { Button } from "@/components/ui/button";

import { producaoApi } from "./api";
import DialogProduzir from "./components/DialogProduzir";
import HistoricoProducao from "./components/HistoricoProducao";
import { fmtBRL, fmtQtd } from "./lib/formato";

/** Estoque > Producao: receitas (com quanto da pra fazer e o estoque do item
 *  pronto), botao Produzir e historico com estorno. */
export default function ProducaoTab({ canEdit, toast, onNovaReceita, onEditarReceita, atualizacao }) {
  const [receitas, setReceitas] = useState(null);
  const [historico, setHistorico] = useState(null); // null = 1a carga (esqueleto)
  const [produzindo, setProduzindo] = useState(null);
  const carregar = useCallback(() => {
    // Falha na 1a busca não pode deixar o esqueleto piscando para sempre.
    producaoApi.receitas().then(setReceitas).catch((e) => { toast.error(e.message); setReceitas((r) => r || []); });
    producaoApi.historico().then(setHistorico).catch(() => setHistorico((h) => h || []));
  }, [toast]);
  useEffect(() => { carregar(); }, [carregar, atualizacao]);

  const tom = (r) => (!r.pronta ? "text-muted-foreground" : r.stock_min > 0 && r.estoque <= r.stock_min ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400");

  return (
    <div className="space-y-4" data-e2e="painel-producao">
      <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/20 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Receitas</h3>
            <p className="text-xs text-muted-foreground">Registre cada produção: baixa os ingredientes base e sobe o estoque do item pronto.</p>
          </div>
          {canEdit && <Button onClick={onNovaReceita} variant="outline" className="shadow-sm" data-e2e="nova-receita"><ChefHat className="w-4 h-4 mr-2" /> Nova Receita</Button>}
        </div>
        {receitas === null ? <CarregandoTabela linhas={4} colunas={6} className="" /> : receitas.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground text-center">Nenhuma receita cadastrada. Clique em "Nova Receita" para começar.</p>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-muted/50 border-b"><tr>
              {["Receita", "Rende", "Pronto em estoque", "Dá pra fazer", "Custo", ""].map((h) => <th key={h} className="text-left py-2.5 px-4 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>)}
            </tr></thead>
            <tbody>{receitas.map((r) => (
              <tr key={r.product_id} className="border-b align-top" data-e2e="linha-receita">
                <td className="py-3 px-4">
                  <p className="font-semibold">{r.name}</p>
                  <p className="text-[11px] text-muted-foreground">{r.n_bases} ingrediente{r.n_bases === 1 ? "" : "s"}{!r.pronta ? " · receita incompleta" : ""}</p>
                </td>
                <td className="py-3 px-4 tabular-nums whitespace-nowrap">{r.rende > 0 ? <>{fmtQtd(r.rende, r.unidade)} {r.unidade}<span className="text-[11px] text-muted-foreground"> / lote</span></> : <span className="text-amber-600 dark:text-amber-400 text-xs">sem rendimento</span>}</td>
                <td className="py-3 px-4 whitespace-nowrap">
                  <p className={`text-lg font-bold tabular-nums leading-none ${tom(r)}`} data-e2e="pronto">{fmtQtd(r.estoque, r.unidade)} {r.unidade}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{r.stock_max > 0 || r.stock_min > 0 ? `mín ${fmtQtd(r.stock_min, r.unidade)} · máx ${r.stock_max > 0 ? fmtQtd(r.stock_max, r.unidade) : "—"}` : "sem mín/máx"}</p>
                </td>
                <td className="py-3 px-4 whitespace-nowrap" data-e2e="da-pra-fazer">
                  {r.lotes_possiveis == null ? <span className="text-muted-foreground">—</span> : (
                    <><p className={`font-semibold tabular-nums ${r.lotes_possiveis < 1 ? "text-amber-600 dark:text-amber-400" : ""}`}>{fmtQtd(r.lotes_possiveis, "")} lote{r.lotes_possiveis === 1 ? "" : "s"}</p>
                      <p className="text-[11px] text-muted-foreground">limitado por {r.limitante}</p></>
                  )}
                </td>
                <td className="py-3 px-4 whitespace-nowrap tabular-nums">
                  <p className="font-semibold">{r.custo_unitario != null ? <>{fmtBRL(r.custo_unitario)}<span className="text-[11px] font-normal text-muted-foreground">/{r.unidade}</span></> : "—"}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtBRL(r.custo_lote)} por lote</p>
                </td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  {canEdit && (
                    <div className="inline-flex items-center gap-1.5">
                      <Button size="sm" onClick={() => setProduzindo(r)} disabled={!r.pronta} data-testid={`produzir-${r.product_id}`}><Flame className="w-3.5 h-3.5 mr-1.5" /> Produzir</Button>
                      <button type="button" onClick={() => onEditarReceita(r.ingredient_id, r.product_id)} title="Editar receita" data-testid={`editar-receita-${r.product_id}`}
                        className="w-8 h-8 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10"><Edit className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>))}</tbody>
          </table></div>
        )}
      </div>

      <HistoricoProducao itens={historico || []} carregando={historico === null} canEdit={canEdit} toast={toast} onEstornado={() => { carregar(); onEditarReceita && null; }} />

      {produzindo && <DialogProduzir receita={produzindo} toast={toast} onClose={() => setProduzindo(null)} onProduzido={() => carregar()} />}
    </div>
  );
}
